import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	RemoteDataSourceState,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	download,
	PixabayUserModel,
	PixabayImageModel,
	searchPixabay
} from "client/Api/ImageGallery/imageGallery";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	Anchor,
	BusyIndicator,
	Button,
	ChangeTriggerKind,
	Dialog,
	Intersection,
	LoadMoreIndicator,
	NoResults,
	SearchInputForm,
	SearchPlaceholder
} from "client/Components";
import { never } from "client_legacy/ndaw/errors";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	generateTrackingId,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { newGuid } from "client_legacy/ndaw/guid";
import styles from "./PixabayGalleryDialog.module.less";
import { ImageAttributionLink } from "../Hub/ImageAttributionLink";
import { ImageAttributionKind } from "client/Api/common";

export type PixabayGalleryImageMetadata = {
	author: PixabayUserModel | null;
};

export type PixabayGalleryImage = {
	file: File;
	metadata: PixabayGalleryImageMetadata;
};

export type PixabayGalleryDialogProps = {
	onClose: () => void;
	onSelect: (image: PixabayGalleryImage) => void;
};

export const PixabayGalleryDialog = (props: PixabayGalleryDialogProps): ReactElement => {
	const {
		onClose,
		onSelect
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const computedSearchTerm = useMemo(
		(): string => {
			const { value } = searchTerm;

			return isNotNilOrWhiteSpace(value)
				? value
				: "";
		},
		[searchTerm]
	);

	const {
		currentItems,
		getLoadingState,
		hasMoreOnServer,
		hasLazyLoadError,
		retryToLoadLastPage,
		reload
	} = useDataSource(
		{
			searchTerm: computedSearchTerm,
			desiredAmountOfItems
		}
	);

	const selectHandler = useCallback(
		async (item: PixabayImageModel): Promise<void> => {
			setIsBusy(true);

			try {
				const file: File = await downloadImageFromUrl(
					item.downloadId,
					resolveFileName(item.cdnPreviewUrl),
					globalCancellation
				);

				onSelect({
					file,
					metadata: {
						author: item.author
					}
				});

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			onSelect
		]
	);

	const selectCategoryHandler = useCallback(
		(categoryName: string) => setSearchTerm({
			value: categoryName,
			version: newGuid()
		}),
		[]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(x => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(computedSearchTerm);

			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[computedSearchTerm]
	);

	const renderContent = (): ReactElement | null => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (currentItems.length > 0) {
			return (
				<>
					<ResultGrid
						items={currentItems}
						onSelect={selectHandler}
					/>

					<LoadMoreIndicator
						isLoadingData={loadingState === "loading"}
						hasMoreItems={hasMoreOnServer}
						onRequestMoreItems={loadMoreHandler}
					/>

					{hasLazyLoadError && (
						<NoResults
							trackingId="has-lazy-loading-error"
							icon="gear"
							title={localize("_NotAbleToRetrieveSearchResults")}
							message={localize("_WeWereUnableToRetrieveSearchResults")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		switch (loadingState) {
			case "loading":
				return (
					<BusyIndicator />
				);
			case "ready":
				return computedSearchTerm
					? (
						<NoResults
							trackingId="no-results"
							icon="magnifying-glass"
							title={localize("_NoSearchResults")}
							message={localize("_PleaseTrySearchingWithAnotherTerm")}
							height="fill"
						/>
					)
					: null;
			case "error":
				return (
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={reload}
						height="fill"
					/>
				);
			default:
				never(loadingState);
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			height="fill"
			size="long"
			trackingId="select-image-from-pixabay-dialog"
		>
			<Dialog.Header
				title={localize("_SelectImageFromPixabay")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<div className={styles.container}>
					<div className={styles.searchFormContainer}>
						<SearchInputForm
							trackingId="search"
							value={searchTerm}
							onChange={(value, version) => setSearchTerm({value, version})}
							trigger={{ kind: ChangeTriggerKind.OnEnter }}
							placeholder={
								<SearchPlaceholder
									kind="search"
									properties={[
										localize("_Images")
									]}
								/>
							}
						/>
					</div>

					<div className={styles.content}>
						{!computedSearchTerm && (
							<div className={styles.categoriesContainer}>
								<CategoryPicker
									onSelect={selectCategoryHandler}
								/>
							</div>
						)}
						{renderContent()}
					</div>
				</div>
			</Dialog.Body>

			<Dialog.Footer>
				<div className={styles.footerContent}>
					<Anchor
						className={styles.licenseLink}
						href="https://pixabay.com/service/terms"
						target="_blank"
					>
						{localize("_PixabayLicenseTerms")}
					</Anchor>
				</div>
			</Dialog.Footer>
		</Dialog>
	);
};
PixabayGalleryDialog.displayName = "PixabayGalleryDialog";

const PAGE_SIZE: number = 20;

type CategoryPickerProps = {
	onSelect: (name: string) => void;
};

const CategoryPicker = (props: CategoryPickerProps): ReactElement => {
	const {
		onSelect
	} = props;

	return (
		<ul
			{...setTrackingId("categories")}
			className={styles.categories}
		>
			{categories.map(
				(x, index): ReactElement => (
					<li key={index}>
						<Button
							key={index}
							kind="secondary"
							text={x}
							onClick={() => onSelect(x)}
							trackingId={generateTrackingId("category", `|${x}`)}
						/>
					</li>
				)
			)}
		</ul>
	);
};
CategoryPicker.displayName = "CategoryPicker";

type ResultGridProps = {
	items: readonly PixabayImageModel[];
	onSelect: (item: PixabayImageModel) => void;
};

const ResultGrid = (props: ResultGridProps): ReactElement => {
	const {
		items,
		onSelect
	} = props;

	return (
		<ul
			{...setTrackingId("images-grid")}
			className={styles.imagesGrid}
		>
			{items.map(
				(x): ReactElement => (
					<li key={x.id}>
						<ResultItem
							item={x}
							onSelect={() => onSelect(x)}
							trackingId={generateTrackingId("image", `|${x.id}`)}
						/>
					</li>
				))
			}
		</ul>
	);
};
ResultGrid.displayName = "ResultGrid";

type ResultItemProps = {
	item: PixabayImageModel;
	onSelect: () => void;
	trackingId: string;
};

const ResultItem = (props: ResultItemProps): ReactElement => {
	const {
		item,
		onSelect,
		trackingId
	} = props;

	const {
		id,
		previewUrl,
		cdnPreviewUrl,
		author
	} = item;

	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [hasTriedFallBackUrl, setHasTriedFallBackUrl] = useState<boolean>(false);

	const renderImageHandler = useCallback(
		(): void => {
			setImageUrl(previewUrl);
		},
		[previewUrl]
	);

	const errorHandler = useCallback(
		(): void => {
			setImageUrl(cdnPreviewUrl);
			setHasTriedFallBackUrl(true);
			console.warn(`Fallback image preview was used for the image with id ${id}`);
		},
		[cdnPreviewUrl, id]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.item}
		>
			<Intersection
				runOnce
				onEnter={renderImageHandler}
			>
				<div className={styles.itemImageContainer}>
					<Anchor
						onClick={onSelect}
						className={styles.itemImageLink}
						trackingId="select-image"
					>
						{imageUrl && (
							<img
								className={styles.itemImage}
								src={imageUrl}
								loading="lazy"
								onError={
									!hasTriedFallBackUrl
										? errorHandler
										: undefined
								}
							/>
						)}
					</Anchor>
					{author && (
						<div className={styles.attributionContainer}>
							<ImageAttributionLink
								attribution={{
									kind: ImageAttributionKind.Pixabay,
									authorId: author.id,
									authorName: author.name
								}}
								trackingId="attribution"
							/>
						</div>
					)}
				</div>
			</Intersection>
		</div>
	);
};
ResultItem.displayName = "ResultItem";

const categories: readonly string[] = sortBy(
	[
		localize("_Abstract"),
		localize("_Animal"),
		localize("_Architecture"),
		localize("_Art"),
		localize("_Business"),
		localize("_Colorful"),
		localize("_Food"),
		localize("_Interior"),
		localize("_Minimal"),
		localize("_Nature"),
		localize("_Plant"),
		localize("_Portrait"),
		localize("_Space"),
		localize("_Technology"),
		localize("_Texture"),
		localize("_Wallpaper")
	],
	x => x
);

const useDataSource = createRemoteDataSource<PixabayImageModel>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook({
		performRequest: (options): Promise<LazyLoadPageResult<PixabayImageModel>> => {
			return searchPixabay(
				options.searchTerm,
				options.skip,
				options.amount,
				options.signal
			);
		}
	});


async function downloadImageFromUrl(
	downloadId: string,
	fileName: string,
	signal: AbortSignal
): Promise<File> {
	try {
		const imageBlob: Blob = await download(downloadId, signal);

		const imageFile: File = new File(
			[imageBlob],
			fileName,
			{ type: imageBlob.type }
		);

		return imageFile;
	} catch (e) {
		console.error("Could not load the selected image", e);
		throw e;
	}
}

function resolveFileName(url: string): string {
	const uri = new URL(url);
	const path: string = uri.pathname;
	const segments: string[] = path.split("/");

	if (segments.length > 0) {
		const lastSegment: string = segments[segments.length - 1];

		if (isNotNilOrWhiteSpace(lastSegment)) {
			return lastSegment;
		}
	}

	return newGuid();
}

export function usePixabayGalleryDialog(): [
	element: ReactElement | null,
	open: (initialData: {}) => Promise<TryResult<PixabayGalleryImage>>
] {
	return useDataInputDialog(
		(_, onClose, onSelect) => (
			<PixabayGalleryDialog
				onClose={onClose}
				onSelect={onSelect}
			/>
		)
	);
}